import { isDefined } from "@clipboard-health/util-ts";
import constate from "constate";
import { useCallback, useState } from "react";

export interface UploadedFile {
  id: string;
  dataUrlBase64: string;
  extension: string;
  contentType: string;
}

interface FileUploaderContextProps {
  /**
   * The maximum number of files that can be uploaded.
   * No limit if it's not provided.
   */
  maxFilesCount?: number;
}
/**
 * This context manages the state of file uploads for documents.
 * It allows components to:
 * 1. Track files that have been uploaded
 * 2. Add new files to the upload queue
 * 3. Remove files from the upload queue
 */
function useFileUploaderData(props: FileUploaderContextProps) {
  const { maxFilesCount } = props;

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const addFile = useCallback((file: UploadedFile) => {
    setUploadedFiles((previousFiles) => [...previousFiles, file]);
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setUploadedFiles((previousFiles) => previousFiles.filter((file) => file.id !== fileId));
  }, []);

  const replaceFile = useCallback((fileId: string, newFile: UploadedFile) => {
    setUploadedFiles((previousFiles) =>
      previousFiles.map((file) => (file.id === fileId ? newFile : file))
    );
  }, []);

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
  }, []);

  return {
    maxFilesCount,
    hasReachedMaxFilesCount: isDefined(maxFilesCount) && uploadedFiles.length >= maxFilesCount,
    uploadedFiles,
    addFile,
    removeFile,
    replaceFile,
    clearFiles,
  };
}

export const [FileUploaderProvider, useFileUploaderContext] = constate(useFileUploaderData);
